import { ApiErrorResponseFromJSON, ApiValidationErrorResponseFromJSON } from '../../api';

export interface IAiError {
    /** HTTP status; undefined when the request never got a response (network, CORS, abort). */
    status?: number;
    /** Machine code such as AI_CONFIGURATION_CONFLICT, when the body carries one. */
    code?: string;
    message: string;
    /** The server's trace ID, which support can use to find the request in the backend logs. */
    traceId?: string;
    /** Validation messages keyed by the server's path (format unverified — callers match known paths only). */
    fieldErrors?: Record<string, string>;
}

/**
 * What each known error code means to the person using this page. The server's own `message` is generic
 * ("The requested AI resource was not found."), so a known code is explained here; an unknown code falls back to
 * the server's text rather than to a guess.
 */
const CODE_MESSAGES: Record<string, string> = {
    AI_NOT_CONFIGURED: 'No AI configuration exists for this account in this environment.',
    AI_ACCESS_DISABLED: 'AI access is turned off for this account. Turn on account issuance and save first.',
    AI_PROFILE_NOT_FOUND: 'That profile is not available. It may be disabled, removed, or its agent may be unavailable.',
    AI_KEY_NOT_FOUND: 'No such key. It is unknown, or not owned by this account.',
    AI_AGENT_UNAVAILABLE: 'The agent for this profile is not currently available.',
    AI_CONFIGURATION_CONFLICT: 'The configuration changed while it was being saved. Reload it and try again.',
    // Deliberately says nothing about retrying: it is safe for revoking and saving, and NOT for issuing a key, which
    // creates a new key every time. This text is shared, so each caller adds the advice that is true for its action.
    AI_GATEWAY_UNAVAILABLE: 'The AI gateway did not respond.',
};

/** One line for the screen: the explanation, then the status and code, then the trace ID when there is one. */
export function describeAiError(e: IAiError): string {
    const head = (e.code && CODE_MESSAGES[e.code]) || e.message;
    const meta = [e.status, e.code].filter(Boolean).join(' ');
    return `${head}${meta ? ` (${meta})` : ''}${e.traceId ? ` Trace ${e.traceId}.` : ''}`;
}

/**
 * Read an error thrown by the generated client. It throws the raw `Response` rather than an `Error`
 * (`runtime.ts`), so `instanceof Error` loses the status, the code and the server's message.
 *
 * The body is read from a clone so the original stays consumable by whoever else holds it.
 */
export async function readAiError(err: unknown, fallback = 'Request failed.'): Promise<IAiError> {
    if (typeof Response !== 'undefined' && err instanceof Response) {
        let body: any;
        try {
            body = await err.clone().json();
        } catch {
            body = undefined;
        }
        if (err.status === 400 && body && body.errors && typeof body.errors === 'object') {
            const parsed = ApiValidationErrorResponseFromJSON(body);
            const fieldErrors: Record<string, string> = {};
            for (const [key, value] of Object.entries(parsed.errors ?? {})) {
                fieldErrors[key] = Array.isArray(value) ? value.join(' ') : String(value);
            }
            const summary = Object.entries(fieldErrors)
                .map(([k, v]) => `${k}: ${v}`)
                .join('; ');
            return {
                status: err.status,
                code: body.code,
                message: summary || body.message || fallback,
                traceId: parsed.traceId,
                fieldErrors,
            };
        }
        const parsed = body ? ApiErrorResponseFromJSON(body) : undefined;
        return {
            status: err.status,
            code: parsed?.code,
            message: parsed?.message || err.statusText || fallback,
            traceId: parsed?.traceId,
        };
    }
    if (err instanceof Error) {
        return { message: err.message || fallback };
    }
    return { message: fallback };
}
